import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { faFilePdf, faPen, faTrash } from '@fortawesome/pro-light-svg-icons';
import { Actions, ofType } from '@ngrx/effects';
import { CellClickEvent, GridComponent } from '@progress/kendo-angular-grid';
import { distinctUntilChanged, map, take } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { UserOperation } from '../../../../core/enums/user-operation';
import {
  openCreateDialogQueryParam,
  skipCreateConfirm,
} from '../../../../core/guards/confirm-create-invoice.guard';
import { InvoiceDocumentOverviewModel } from '../../../../core/models/invoice/invoice-document-overview.model';
import { templateIdQueryParam } from '../../../../core/resolver/invoice/invoice-data.resolver';
import { ShiveRoutes } from '../../../../core/routing/segments/route-segments';
import { ShiveDialogService } from '../../../../core/services/controls/shive-dialog.service';
import { EndpointService } from '../../../../core/services/endpoints/endpoint.service';
import { FileService } from '../../../../core/services/file.service';
import { InvoiceDocumentHttpService } from '../../../../core/state/invoice-documents/invoice-document-http.service';
import * as fromInvoiceDocumentActions from '../../../../core/state/invoice-documents/invoice-document.actions';
import { ShiveGridWrapperComponent } from '../../../../shared/components/shive-controls/shive-grid/shive-grid-wrapper.component';
import { FileEntityKey, FileEntityKind } from '../../../../shared/data-types/file-types';
import { HttpParamObject } from '../../../../shared/data-types/http-types';
import { findKvRecordValue } from '../../../../shared/functions/record-functions';
import { CreateInvoiceDialogComponent } from '../dialogs/create-invoice-dialog/create-invoice-dialog.component';
import { TitleMetaKey } from '../invoice.config';

@Component({
  selector: 'app-invoice-list',
  templateUrl: './invoice-list.component.html',
  standalone: false,
})
export class InvoiceListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('grid') grid: GridComponent;
  @ViewChild('gridWrapperComponent') gridWrapperComponent: ShiveGridWrapperComponent;

  public gridData: InvoiceDocumentOverviewModel[] = [];
  public readonly ShiveRoutes = ShiveRoutes;
  public readonly EndpointService = EndpointService;
  public readonly findKvRecordValue = findKvRecordValue;
  public readonly TitleMetaKey = TitleMetaKey;

  // DI
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly actions$ = inject(Actions);
  private readonly invoiceDocumentHttpService = inject(InvoiceDocumentHttpService);
  private readonly shiveDialogService = inject(ShiveDialogService);
  private readonly fileService = inject(FileService);

  private readonly subs = new SubSink();
  private dialogOpened = false;

  ngOnInit(): void {
    this.handleDialogOpenEvents();
    this.fetchInvoices();
    this.initEvents();
  }

  ngAfterViewInit(): void {
    this.gridWrapperComponent.contextMenuItems = [
      {
        text: 'Bearbeiten',
        faIcon: faPen,
        callback: (invoiceOverviewModel: InvoiceDocumentOverviewModel) => {
          void this.router.navigate(['../', ShiveRoutes.Detail, invoiceOverviewModel.id], {
            relativeTo: this.route,
          });
        },
        isVisible: true,
        isActivated: true,
      },
      {
        text: 'Download',
        faIcon: faFilePdf,
        callback: (invoiceOverviewModel: InvoiceDocumentOverviewModel) => {
          const params: HttpParamObject = {
            kind: FileEntityKind.Invoice,
            [FileEntityKey.FileId]: invoiceOverviewModel.attachments.id,
          };
          this.fileService.fetchFile(params, true);
        },
        isVisible: (invoiceOverviewModel: InvoiceDocumentOverviewModel) =>
          !!invoiceOverviewModel.attachments?.fileName,
        isActivated: true,
      },
      {
        text: 'Löschen',
        faIcon: faTrash,
        callback: (invoiceDocument: InvoiceDocumentOverviewModel) => {
          this.invoiceDocumentHttpService.deleteInvoiceDocumentOverviewModel(invoiceDocument);
        },
        isVisible: true,
        isActivated: true,
        iconColorClass: 'red',
      },
    ];
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public onCellClick(e: CellClickEvent): void {
    if (e.columnIndex === this.grid.columns.length - 1) {
      return;
    }
    const invoiceOverviewModel = e.dataItem as InvoiceDocumentOverviewModel;
    void this.router.navigate(['../', ShiveRoutes.Detail, invoiceOverviewModel.id], {
      relativeTo: this.route,
    });
  }

  public openCreateInvoiceDialog(): void {
    const ref = this.shiveDialogService.open({
      title: 'Neue Rechnung',
      content: CreateInvoiceDialogComponent,
    });

    ref.result
      .pipe(
        take(1),
        map((result) => result !== UserOperation.Cancel),
      )
      .subscribe((ok) => {
        // Clear the flag from the URL regardless (so refresh doesn’t re-open)
        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { [openCreateDialogQueryParam]: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });

        if (ok) {
          // If a template was selected, load the template and merge it with invoice positions of the selected project.
          const selectedTemplateId = (ref.content.instance as CreateInvoiceDialogComponent)
            .selectedTemplateOption?.value;
          void this.router.navigate(
            [ShiveRoutes.Finances, ShiveRoutes.Invoices, ShiveRoutes.Create],
            {
              state: { [skipCreateConfirm]: true },
              queryParams: { [templateIdQueryParam]: selectedTemplateId },
            },
          );
        }
        this.dialogOpened = false;
      });
  }

  private fetchInvoices() {
    this.invoiceDocumentHttpService
      .getInvoiceDocumentOverviewModels()
      .pipe(take(1))
      .subscribe((invoices) => {
        this.gridData = invoices;
      });
  }

  private initEvents() {
    this.subs.sink = this.actions$
      .pipe(
        ofType(
          fromInvoiceDocumentActions.invoiceDocumentGenerated,
          fromInvoiceDocumentActions.invoiceDocumentOverviewDeleted,
        ),
        map((entry) => entry.payload[0]),
      )
      .subscribe(() => {
        this.fetchInvoices();
      });
  }

  private handleDialogOpenEvents(): void {
    // Handle initial load
    this.route.queryParamMap.pipe(take(1)).subscribe((qp) => {
      const shouldOpen = qp.get(openCreateDialogQueryParam) === '1';
      if (!shouldOpen) {
        return;
      }

      this.openCreateInvoiceDialog();
      this.dialogOpened = true;
    });

    // React to subsequent query-param changes.
    this.subs.sink = this.route.queryParamMap
      .pipe(
        map((qp) => qp.get(openCreateDialogQueryParam) === '1'),
        distinctUntilChanged(),
      )
      .subscribe((shouldOpen) => {
        if (shouldOpen && !this.dialogOpened) {
          this.openCreateInvoiceDialog();
        }
      });
  }
}
